import { useMemo, useState } from 'react';
import { Badge, Button, Center, Group, Loader, Paper, Pill, SimpleGrid, Stack, Text, TextInput } from '@mantine/core';
import { IconSearch, IconStar } from '@tabler/icons-react';
import { useSubscription } from 'urql';
import { graphql } from '../lib/graphql';
import { readTemplateVariables } from './template-variable-editor';

const BrowsePersonaTemplatesSubscription = graphql(`
  subscription BrowsePersonaTemplates {
    personaTemplates(orderBy: [{ featured: DESC }, { sortOrder: ASC }]) {
      id
      name
      description
      category
      featured
      variables
    }
  }
`);

const BrowseFlowTemplatesSubscription = graphql(`
  subscription BrowseFlowTemplates {
    flowTemplates(orderBy: [{ featured: DESC }, { sortOrder: ASC }]) {
      id
      name
      description
      category
      featured
      variables
    }
  }
`);

type BrowsableTemplate = {
  id: string;
  name: string;
  description: string;
  category: string | null;
  featured: boolean;
  variableCount: number;
};

type TemplateBrowserProps = {
  type: 'persona' | 'flow';
  onSelect: (templateId: string) => void;
  onCancel: () => void;
};

export function TemplateBrowser({ type, onSelect, onCancel }: TemplateBrowserProps) {
  const [{ data: personaData }] = useSubscription({
    query: BrowsePersonaTemplatesSubscription,
    pause: type !== 'persona',
  });

  const [{ data: flowData }] = useSubscription({
    query: BrowseFlowTemplatesSubscription,
    pause: type !== 'flow',
  });

  const templates = useMemo<BrowsableTemplate[] | undefined>(() => {
    const rows = type === 'persona' ? personaData?.personaTemplates : flowData?.flowTemplates;

    return rows?.map((t) => ({
      id: t.id,
      name: t.name,
      description: t.description,
      category: t.category ?? null,
      featured: t.featured,
      variableCount: readTemplateVariables(t.variables).length,
    }));
  }, [type, personaData, flowData]);

  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  const categories = useMemo(
    () => [...new Set((templates ?? []).flatMap((t) => (t.category ? [t.category] : [])))].sort(),
    [templates],
  );

  const filtered = useMemo(
    () =>
      (templates ?? []).filter((t) => {
        if (selectedCategory && t.category !== selectedCategory) return false;

        return (
          !search ||
          t.name.toLowerCase().includes(search.toLowerCase()) ||
          t.description.toLowerCase().includes(search.toLowerCase())
        );
      }),
    [templates, search, selectedCategory],
  );

  if (templates === undefined) {
    return (
      <Center py="xl">
        <Loader size="lg" />
      </Center>
    );
  }

  return (
    <Stack gap="md">
      <TextInput
        placeholder="Search templates..."
        leftSection={<IconSearch size={16} />}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      {categories.length > 0 && (
        <Group gap="xs">
          <Pill
            onClick={() => setSelectedCategory(null)}
            style={{ cursor: 'pointer' }}
            bg={!selectedCategory ? 'blue' : undefined}
            c={!selectedCategory ? 'white' : undefined}
          >
            All
          </Pill>
          {categories.map((category) => (
            <Pill
              key={category}
              onClick={() => setSelectedCategory(selectedCategory === category ? null : category)}
              style={{ cursor: 'pointer' }}
              bg={selectedCategory === category ? 'blue' : undefined}
              c={selectedCategory === category ? 'white' : undefined}
            >
              {category}
            </Pill>
          ))}
        </Group>
      )}

      {filtered.length === 0 && (
        <Text c="dimmed" ta="center" py="xl">
          {templates.length === 0 ? `No ${type} templates available yet.` : 'No templates match your search.'}
        </Text>
      )}

      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
        {filtered.map((template) => (
          <Paper
            key={template.id}
            p="md"
            withBorder
            style={{ cursor: 'pointer' }}
            onClick={() => onSelect(template.id)}
          >
            <Stack gap="xs">
              <Group justify="space-between" wrap="nowrap">
                <Text fw={600} size="sm" lineClamp={1}>
                  {template.name}
                </Text>
                {template.featured && (
                  <Badge size="xs" color="yellow" leftSection={<IconStar size={10} />}>
                    Featured
                  </Badge>
                )}
              </Group>
              <Text size="xs" c="dimmed" lineClamp={2}>
                {template.description}
              </Text>
              <Group gap="xs">
                {template.category && (
                  <Badge size="xs" variant="light">
                    {template.category}
                  </Badge>
                )}
                <Badge size="xs" variant="light" color="gray">
                  {template.variableCount} variable{template.variableCount !== 1 ? 's' : ''}
                </Badge>
              </Group>
            </Stack>
          </Paper>
        ))}
      </SimpleGrid>

      <Group justify="flex-end">
        <Button variant="subtle" onClick={onCancel}>
          Cancel
        </Button>
      </Group>
    </Stack>
  );
}
